// Who may open each admin surface. One entry per surface, read by BOTH the
// admin hub tile (AdminPage) and the route guard (AdminSurfaceRoute in App.tsx),
// so a tile is shown exactly when its page would let the user in.
//
// A rule is a union of grants: the user passes if they are in any listed
// business group OR hold the managed role (admins hold managed roles implicitly,
// see userHasManagedRole). `everyone` passes any signed-in user. A `flag` that is
// off hides the tile and makes the route a 404 for everybody.
//
// The backend is still the authority for every API behind these pages; this only
// keeps the hub honest about which pages a user can open. Keep each rule in step
// with the backend gate it mirrors — see AUTH_SPEC.md.

import type { User } from '../shared/api-client';
import { managedRolesUserCanDelegate, userHasManagedRole } from '../permissions/managed-role-hooks';

export interface AdminSurfaceAccess {
  groups?: readonly string[];
  managedRole?: { roleId: string; tierId?: string };
  everyone?: boolean;
  // Passes a user who can grant some managed role to others (holds its top tier).
  delegatesManagedRole?: boolean;
  flag?: string;
}

const ADMIN_ONLY: AdminSurfaceAccess = { groups: ['admin'] };
const ADMIN_OR_DEVOPS: AdminSurfaceAccess = { groups: ['admin', 'devops'] };
// Any tier. Viewers get the pages read-only; what a Manager may change is gated
// inside each page and, authoritatively, on the API.
const SUPPORT_AGENT: AdminSurfaceAccess = { managedRole: { roleId: 'support-agent' } };

export const ADMIN_SURFACE_REGISTRY = {
  // People & Access
  users: ADMIN_ONLY,
  bots: ADMIN_ONLY,
  'access-control': ADMIN_ONLY,
  // Admins curate every role; a top-tier holder sees only the roles they can grant.
  roles: { groups: ['admin'], delegatesManagedRole: true },
  usage: ADMIN_ONLY,

  // AI & Chat
  chat: ADMIN_ONLY,
  assistants: { groups: ['admin', 'product-engineering'] },
  personas: ADMIN_ONLY,
  tasks: ADMIN_ONLY,
  'user-tasks': ADMIN_ONLY,
  'deck-theme': ADMIN_ONLY,
  'rfp-qualifier': ADMIN_ONLY,
  a2a: { managedRole: { roleId: 'a2a' } },
  'chat-analytics': { managedRole: { roleId: 'analytics' } },
  // Admin-only: recorded call arguments are whatever a user's agent passed to a tool.
  mcp: { ...ADMIN_ONLY, flag: 'MCP_SERVERS' },
  'mcp-usage': { ...ADMIN_ONLY, flag: 'MCP_SERVERS' },
  'scheduled-prompts': ADMIN_ONLY,
  'chat-attachments': ADMIN_ONLY,

  // Integrations
  salesforce: ADMIN_ONLY,
  atlassian: ADMIN_ONLY,
  slack: ADMIN_ONLY,
  'google-drive': ADMIN_ONLY,
  // Viewer tier is enough — the list is read-only, and starting a meeting is
  // gated separately inside the page.
  'google-meets': { managedRole: { roleId: 'google-meets' } },
  hubspot: ADMIN_ONLY,
  'hubspot-channel': ADMIN_ONLY,
  github: ADMIN_OR_DEVOPS,
  cloudflare: ADMIN_ONLY,
  elasticsearch: ADMIN_OR_DEVOPS,
  kibo: ADMIN_ONLY,
  'api-explorer-catalog': ADMIN_ONLY,
  wordpress: ADMIN_ONLY,
  webtool: ADMIN_ONLY,
  mintlify: ADMIN_ONLY,
  fireworks: ADMIN_ONLY,
  'site-editor-config': ADMIN_ONLY,
  'workspace-chat-config': ADMIN_ONLY,
  'fireworks-proxy': { managedRole: { roleId: 'fireworks-proxy' } },
  'communications-config': { ...ADMIN_ONLY, flag: 'CLIENT_COMMUNICATIONS' },
  // Reading the outbox is the `outbound-mail` role; changing what mail does is not.
  'outbound-mail': { managedRole: { roleId: 'outbound-mail' } },
  'outbound-mail-config': ADMIN_ONLY,
  // Settling says what happened in another system, and "send it again" writes there.
  effects: ADMIN_ONLY,
  audit: ADMIN_ONLY,
  'support-notifications': SUPPORT_AGENT,

  // Support, Sales & Marketing
  support: SUPPORT_AGENT,
  // Reached from the AI Support Agent hub rather than a tile of their own.
  'support-config': SUPPORT_AGENT,
  'support-tickets': SUPPORT_AGENT,
  'support-lab-setup': SUPPORT_AGENT,
  // Viewer tier sees the queue; approving is gated inside on the manager tier.
  'support-approvals': { managedRole: { roleId: 'support-ps-approver' } },
  'support-traces': { managedRole: { roleId: 'support-deflection-trace' } },
  // Minting a key mints a credential into every tenant's tickets.
  'support-keys': ADMIN_ONLY,
  // Manual transcription surface for validating the self-transcription pipeline.
  'sales-intelligence-admin': { ...ADMIN_ONLY, flag: 'SALES_INTELLIGENCE' },
  'linkedin-config': { ...ADMIN_ONLY, flag: 'LINKEDIN_POST_GENERATOR' },
  // Open to the role's manager tier — the "who is using it" view managers
  // asked for, without giving them the admin-only config next door.
  'linkedin-usage': { managedRole: { roleId: 'linkedin-post-generator', tierId: 'manager' }, flag: 'LINKEDIN_POST_GENERATOR' },
  // Creator tier owns the whole enrichment workflow, including its config and
  // the Swan-facing API keys (the config page manages both). Admins pass
  // implicitly (userHasManagedRole).
  'company-enrichment-config': { managedRole: { roleId: 'company-enrichment', tierId: 'creator' } },
  opsgenie: ADMIN_ONLY,
  // Admin-only: this config decides which channel gets paged and which mentions
  // get escalated to, so it is not part of the incidents feature's open access.
  'incidents-config': ADMIN_ONLY,
  // View access; edit controls inside are gated on the pricing-calculator-manager role.
  pricing: {
    groups: ['admin', 'sales', 'sales-engineering'],
    managedRole: { roleId: 'pricing-calculator-manager' },
  },

  // Observability & Infra
  'grafana-endpoints': ADMIN_OR_DEVOPS,
  'grafana-tuning': ADMIN_ONLY,
  'grafana-triage-config': { managedRole: { roleId: 'grafana-triage', tierId: 'manager' }, flag: 'GRAFANA_TRIAGE' },
  // Any tier reads; triage and config are gated on the manager tier inside the pages and
  // on the API. Admins need a grant (adminsNeedGrant): findings carry production IPs.
  'security-watch': { managedRole: { roleId: 'security-watch' }, flag: 'SECURITY_WATCH' },
  canaries: ADMIN_OR_DEVOPS,
  'alert-test': ADMIN_OR_DEVOPS,
  temporal: ADMIN_ONLY,
  // Build mode plus the public /health and /ready probes — nothing a signed-in
  // user cannot already fetch.
  debug: { everyone: true },
  environments: { managedRole: { roleId: 'account-modification' } },
  'tenant-pods': { managedRole: { roleId: 'account-modification' } },
  // DevOps pass the key-management gate without a grant (see ticket-deployment-routes.ts).
  'ticket-deployments': { groups: ['devops'], managedRole: { roleId: 'ticket-deployment-keys' } },
  'dev-accounts': { managedRole: { roleId: 'account-modification' } },
  'kibo-tenants': ADMIN_ONLY,
  'tenant-login-mapping': ADMIN_ONLY,
  looker: ADMIN_ONLY,

  // Platform & Data
  'feature-flags': ADMIN_ONLY,
  'credential-sets': ADMIN_ONLY,
  callouts: ADMIN_ONLY,
  'accounts-import-export': { managedRole: { roleId: 'account-modification' } },
  partners: { managedRole: { roleId: 'account-modification' } },
  'delivery-pods': { everyone: true },
  documentation: { everyone: true },
  'code-repos': ADMIN_OR_DEVOPS,
} satisfies Record<string, AdminSurfaceAccess>;

export type AdminSurfaceId = keyof typeof ADMIN_SURFACE_REGISTRY;

export type AdminSurfaceVerdict = 'allowed' | 'forbidden' | 'not-found';

export function adminSurfaceVerdict(
  user: User | null,
  surfaceId: AdminSurfaceId,
  isFlagEnabled: (flag: string) => boolean,
): AdminSurfaceVerdict {
  const access: AdminSurfaceAccess = ADMIN_SURFACE_REGISTRY[surfaceId];
  if (access.flag && !isFlagEnabled(access.flag)) return 'not-found';
  if (!user) return 'forbidden';
  if (access.everyone) return 'allowed';
  if (access.groups?.some((g) => user.groups.includes(g))) return 'allowed';
  if (access.managedRole && userHasManagedRole(user, access.managedRole.roleId, access.managedRole.tierId)) {
    return 'allowed';
  }
  if (access.delegatesManagedRole && managedRolesUserCanDelegate(user).length > 0) return 'allowed';
  return 'forbidden';
}
